import { without, any, property, isFunction } from 'underscore';
import React, { useCallback, useMemo, useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { AttentionBeaconContext } from './attention-beacon-context';
import { getBeaconId } from './beacon-id';

export const AttentionBeaconGroup = ({ children, attentionBeaconProps }) => {
  const { debugMode: debugModeFromProps, onNeedsAttentionChange } = attentionBeaconProps;
  // We currently support either a root-node-mode or a proxy mode
  // - in the root-node-mode the parentContext is undefined
  // - in the proxy mode the parentContext is defined, isActive can be used for external control
  //   of the proxy's sub-tree ignoring parent's isActive and focusing tracking
  const parentContext = useContext(AttentionBeaconContext);
  const isIsolated = !parentContext;
  const {
    registerBeacon: parentRegisterBeacon,
    unregisterBeacon: parentUnregisterBeacon,
    notifyStateChange: parentNotifyStateChange,
    debugMode: parentDebugMode,
  } = isIsolated ? {} : parentContext;

  const beaconsRef = useRef([]);
  const needsAttentionRef = useRef(false);
  // const [needsAttentionState, setNeedsAttentionState] = useState(needsAttentionRef.current);

  const notifyStateChange = useCallback(() => {
    needsAttentionRef.current = any(beaconsRef.current, b => b.needsAttention());
    // setNeedsAttentionState(needsAttentionRef.current);
    if (isFunction(onNeedsAttentionChange)) {
      onNeedsAttentionChange(needsAttentionRef.current);
    }
    if (isFunction(parentNotifyStateChange)) {
      parentNotifyStateChange();
    }
  }, [parentNotifyStateChange, onNeedsAttentionChange]);

  const registerBeacon = useCallback((beacon) => {
    beaconsRef.current = [...beaconsRef.current, beacon];
    notifyStateChange();
  }, [notifyStateChange]);
  const unregisterBeacon = useCallback((beacon) => {
    beaconsRef.current = without(beaconsRef.current, beacon);
    notifyStateChange();
  }, [notifyStateChange]);

  const needsAttentionQuery = useCallback(() => needsAttentionRef.current, []);

  const [beaconId, setBeaconId] = useState(-1);
  useEffect(() => {
    const id = getBeaconId();
    setBeaconId(id);
    const beacon = { id, needsAttention: needsAttentionQuery };
    if (!isIsolated) {
      parentRegisterBeacon(beacon);
    }
    return () => {
      if (!isIsolated) {
        parentUnregisterBeacon(beacon);
      }
    };
  }, [needsAttentionQuery, isIsolated, parentRegisterBeacon, parentUnregisterBeacon]);

  const debugMode = debugModeFromProps || parentDebugMode;
  const attentionBeaconContextProps = useMemo(() => ({
    registerBeacon,
    unregisterBeacon,
    notifyStateChange,
    debugMode,
  }), [registerBeacon, unregisterBeacon, notifyStateChange, debugMode]);

  return (
    <AttentionBeaconContext.Provider value={attentionBeaconContextProps}>
      {debugMode &&
        <div>{`AttGrp ID: ${beaconId}. Beacons: ${beaconsRef.current.map(property('id')).join(', ')}`}</div>
      }
      {children}
    </AttentionBeaconContext.Provider>
  );
};

AttentionBeaconGroup.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  attentionBeaconProps: PropTypes.shape({
    onNeedsAttentionChange: PropTypes.func,
    debugMode: PropTypes.bool,
  }),
};

AttentionBeaconGroup.defaultProps = {
  attentionBeaconProps: {
    onNeedsAttentionChange: undefined,
    debugMode: false,
  },
};

export const withAttentionBeaconGroupProxy = (WrappedComponent) => {
  const HOC = ({ attentionBeaconProps, ...restProps }) => (
    <AttentionBeaconGroup attentionBeaconProps={attentionBeaconProps} >
      <WrappedComponent {...restProps} />
    </AttentionBeaconGroup>
  );

  HOC.propTypes = {
    attentionBeaconProps: PropTypes.shape({
      onNeedsAttentionChange: PropTypes.func,
      debugMode: PropTypes.bool,
    }),
  };

  HOC.defaultProps = {
    attentionBeaconProps: {
      onNeedsAttentionChange: undefined,
      debugMode: false,
    },
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withAttentionBeaconGroup(${getDisplayName(WrappedComponent)})`;
  // HOC.isAttentionBeaconConsumer = true;

  return HOC;
};
